import React, { useEffect, useRef, Suspense } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Layout } from 'antd';
import Sidebar from '../components/sidebar';
import AppHeader from '../components/app-header';
import TabMenu from '../components/tab-menu';
import ChatIcons from '../views/chat/chat-icons';
import Footer from '../components/footer';
import { data } from '../configs/menu-config';
import { setUserData, clearUser } from '../redux/slices/auth';
import { fetchAppShop } from '../redux/slices/appShop';
import Loading from '../components/loading';
import SubscriptionsDate from '../components/subscriptions-date';
import userService from '../services/user';

const AUTH_TOKEN = 'token';
const { Content } = Layout;

const AppLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sessionValidated = useRef(false);
  const { user } = useSelector((state) => state.auth, shallowEqual);
  const { direction, navCollapsed } = useSelector(
    (state) => state.theme.theme,
    shallowEqual,
  );

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem(AUTH_TOKEN);
    if (!token) {
      dispatch(clearUser());
      navigate('/login', { replace: true });
      return;
    }
    if (sessionValidated.current) return;
    sessionValidated.current = true;
    userService
      .profileShow()
      .then(() => {
        const userObj = {
          ...user,
          urls: data[user.role],
        };
        dispatch(setUserData(userObj));
        dispatch(fetchAppShop());
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem(AUTH_TOKEN);
          dispatch(clearUser());
          navigate('/login', { replace: true });
        } else {
          const userObj = {
            ...user,
            urls: data[user.role],
          };
          dispatch(setUserData(userObj));
          // Avoid cascading startup requests when profile endpoint is timing out/upstream failing.
          const isUpstreamUnavailable =
            err?.response?.status >= 500 ||
            err?.code === 'ECONNABORTED' ||
            String(err?.message || '').toLowerCase().includes('timeout') ||
            String(err?.message || '').toLowerCase().includes('network');
          if (!isUpstreamUnavailable) {
            dispatch(fetchAppShop());
          }
        }
      });
  }, [user, dispatch, navigate]);

  const getLayoutGutter = () => {
    // return navCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH
    return navCollapsed ? 80 : 250;
  };

  const getLayoutDirectionGutter = () => {
    if (direction === 'ltr') {
      return { paddingLeft: getLayoutGutter(), minHeight: '100vh' };
    }
    if (direction === 'rtl') {
      return { paddingRight: getLayoutGutter(), minHeight: '100vh' };
    }
    return { paddingLeft: getLayoutGutter() };
  };

  return (
    <Layout className='app-container'>
      <Sidebar />
      <Layout className='app-layout' style={getLayoutDirectionGutter()}>
        {/* Header sits above the tab bar: the breadcrumb says where you are,
            the tabs say what else you have open. */}
        <AppHeader />
        <TabMenu />
        <Content className='p-3' style={{ flex: '1 0 70%' }}>
          <Suspense fallback={<Loading />}>
            <SubscriptionsDate />
            <Outlet />
          </Suspense>
        </Content>
        <Footer />
      </Layout>
      {user?.role === 'admin' && <ChatIcons />}
    </Layout>
  );
};

export default AppLayout;
